import { CaretRightIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useState, type ReactNode } from "react";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { IconAqsati } from "@/components/Icons";
import { Select, SelectItem } from "@/components/Select";
import { type PaymentType, usePaymentContext } from "@/contexts/PaymentContext";
import { InstallmentBreakdownDrawer } from "@/features/flights/components/flight-purchase/InstallmentBreakdownDrawer";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

type PaymentTypeRadioProps = {
  value: PaymentType;
  checked: boolean;
  onChange: (type: PaymentType) => void;
  disabled?: boolean;
  label: ReactNode;
  description: ReactNode;
  footer: ReactNode;
};

const PaymentTypeRadio = ({
  value,
  checked,
  onChange,
  disabled,
  label,
  description,
  footer,
}: PaymentTypeRadioProps) => {
  return (
    <label
      className={clsx(
        "relative flex w-1/2 min-w-0 flex-1 flex-col items-start justify-between gap-4 rounded-xl border border-stroke-neutral-soft bg-white p-4 transition-all",
        "has-checked:border-core-accent-500 has-checked:bg-core-accent-100 has-checked:outline has-checked:outline-core-accent-500",
        "has-disabled:cursor-not-allowed has-disabled:opacity-60",
        "only-of-type:flex-row only-of-type:items-center",
      )}
    >
      <input
        type="radio"
        name="paymentType"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="peer hidden"
        disabled={disabled}
      />
      <div>
        <div className="mb-1 text-base font-semibold text-text-primary">
          {label}
        </div>
        <div className="text-xs text-text-tertiary">{description}</div>
      </div>
      <div className="text-base font-semibold text-text-primary">{footer}</div>
    </label>
  );
};

type PaymentChooserProps = {
  price: number;
  onChange?: (paymentType: PaymentType) => void;
};

export const PaymentChooser = ({ price, onChange }: PaymentChooserProps) => {
  const intl = useIntl();
  const {
    paymentType,
    handlePaymentTypeChange,
    selectedInstallments,
    availableInstallments,
    isEligible,
    setInstallments,
    maxAvailableInstallments,
  } = usePaymentContext();

  const [showBreakdown, setShowBreakdown] = useState(false);

  const isBnplFeatureEnabled = useFeatureIsOn("bnpl") && Boolean(isEligible);

  const firstInstallmentMonth = dayjs().add(1, "month");
  const lastInstallmentMonth = firstInstallmentMonth.add(
    selectedInstallments,
    "month",
  );

  const handleChange = (type: PaymentType) => {
    handlePaymentTypeChange(type);
    onChange?.(type);
  };

  return (
    <div>
      <div className="section-card">
        <div className="mb-4 text-lg font-medium text-text-primary">
          {intl.formatMessage({
            defaultMessage: "Select payment method",
            description:
              "Section heading for the payment method selection area in the booking flow",
          })}
        </div>

        <div className="flex w-full gap-2">
          <PaymentTypeRadio
            value="standard"
            checked={paymentType === "standard"}
            onChange={handleChange}
            label={intl.formatMessage({
              defaultMessage: "Standard",
              description: "Short label for the standard upfront payment type",
            })}
            description={intl.formatMessage({
              defaultMessage: "Pay the full amount upfront",
              description:
                "Description of the standard pay-upfront payment option",
            })}
            footer={formatCurrency(price)}
          />

          {isBnplFeatureEnabled ? (
            <PaymentTypeRadio
              value="bnpl"
              checked={paymentType === "bnpl"}
              onChange={handleChange}
              label={
                <span className="flex items-center gap-1">
                  {intl.formatMessage({
                    defaultMessage: "Pay later",
                    description:
                      "Short label for the pay-later payment type in the payment list",
                  })}
                  <IconAqsati className="size-4 shrink-0" />
                </span>
              }
              description={intl.formatMessage({
                defaultMessage:
                  "Split the total cost into manageable, flexible payments",
                description:
                  "Description of the split / installment payment option explaining how it works",
              })}
              footer={intl.formatMessage(
                {
                  defaultMessage: "<from>From</from>{amount} /monthly",
                  description:
                    "Footer text for the pay-later payment option showing the monthly installment amount with a 'From' prefix",
                },
                {
                  from: (chunks) => (
                    <div className="text-xs font-normal text-text-tertiary">
                      {chunks}
                    </div>
                  ),
                  amount: formatCurrency(
                    calculateMonthlyInstallment(
                      includeBnplFee(price),
                      maxAvailableInstallments,
                    ),
                  ),
                },
              )}
            />
          ) : null}
        </div>
      </div>

      {paymentType === "bnpl" ? (
        <div className="section-card mt-2">
          <p className="text-lg font-medium text-text-primary">
            {intl.formatMessage({
              defaultMessage: "Installment calculator",
              description:
                "Section heading for the interactive installment payment calculator",
            })}
          </p>
          <p className="mb-4 text-xs text-text-tertiary">
            {intl.formatMessage({
              defaultMessage: "Select how long you would like to pay",
              description:
                "Instructional text below the payment calculator heading",
            })}
          </p>
          <Select
            label={intl.formatMessage({
              defaultMessage: "Number of installments",
              description:
                "Label for the number of installments in the payment plan",
            })}
            value={selectedInstallments}
            onChange={(e) => setInstallments(Number(e.target.value))}
          >
            {availableInstallments?.map((num) => (
              <SelectItem key={num} value={num}>
                {intl.formatMessage(
                  {
                    defaultMessage: "{count} months",
                    description: "Month count label in the installment plan",
                  },
                  { count: num },
                )}
              </SelectItem>
            ))}
          </Select>

          <button
            type="button"
            onClick={() => setShowBreakdown(true)}
            className="mt-4 flex w-full items-center justify-between gap-4 rounded-2xl border border-stroke-neutral-soft bg-surface-background p-3 text-start"
          >
            <div>
              <div className="text-base font-semibold text-text-label">
                {intl.formatMessage(
                  {
                    defaultMessage: "{value} / monthly",
                    description:
                      "Short 'per month' unit label used next to installment prices",
                  },
                  {
                    value: formatCurrency(
                      calculateMonthlyInstallment(
                        includeBnplFee(price),
                        selectedInstallments,
                      ),
                    ),
                  },
                )}
              </div>
              <div className="mt-1 text-sm text-text-primary">
                {intl.formatMessage(
                  {
                    defaultMessage:
                      "{firstInstallmentMonth, date, ::yyyyMMM} - {lastInstallmentMonth, date, ::yyyyMMM}",
                    description:
                      "Label showing the month range for the installment payments (e.g. 'Jul 2024 - Jun 2025')",
                  },
                  {
                    firstInstallmentMonth: firstInstallmentMonth.toDate(),
                    lastInstallmentMonth: lastInstallmentMonth.toDate(),
                  },
                )}
              </div>
            </div>

            <div className="flex items-center gap-0.5 text-sm font-medium text-text-interactive">
              {intl.formatMessage({
                defaultMessage: "Details",
                description:
                  "Label for the button to view the monthly installment breakdown",
              })}
              <CaretRightIcon
                weight="bold"
                className="size-3.5 shrink-0 rtl:-scale-x-100"
              />
            </div>
          </button>
        </div>
      ) : null}

      <InstallmentBreakdownDrawer
        totalPrice={includeBnplFee(price)}
        open={showBreakdown}
        onClose={() => setShowBreakdown(false)}
      />
    </div>
  );
};
